import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { base44 } from '@/api/base44Client';
import { formatTanggalIndo } from '@/lib/timeUtils';

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);

const ROMAN_MONTHS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];

const loadImageAsBase64 = async (url, maxDim = 400) => {
  if (!url) return null;
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      try {
        let w = img.naturalWidth || img.width;
        let h = img.naturalHeight || img.height;
        if (maxDim && (w > maxDim || h > maxDim)) {
          if (w > h) {
            h = Math.round((h * maxDim) / w);
            w = maxDim;
          } else {
            w = Math.round((w * maxDim) / h);
            h = maxDim;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        resolve(null);
      }
    };
    img.onerror = async () => {
      try {
        const resp = await fetch(url);
        const blob = await resp.blob();
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      } catch {
        resolve(null);
      }
    };
    img.src = url;
  });
};

export const calculateSizePricing = ({
  sizeData,
  botolList = [],
  tutupList = [],
  sprayList = [],
  bahanList = [],
  itemTambahanList = [],
  fullsetListPrice = [],
}) => {
  const getHargaJual = (item) => item?.harga_jual || (item?.harga_rupiah ? item.harga_rupiah * 1.3 : 0);

  const rawSize = sizeData.size;
  const sizeNum = parseInt(String(rawSize).replace(/[^0-9]/g, '')) || 0;
  const ukuran_ml = (sizeData.bahan?.botol_ukuran_aktual_ml ? Number(sizeData.bahan.botol_ukuran_aktual_ml) : (Number(sizeData.bahan?.botol_ukuran_label_ml) || Number(sizeData.racikan?.ukuran_ml) || sizeNum || 0));

  let defaultHppRacikan = 0;
  if (sizeData.racikan?.racikan?.length) {
    sizeData.racikan.racikan.forEach(r => {
      const bahanInfo = bahanList.find(b => (b.nama || '').trim().toLowerCase() === (r.nama_bahan || '').trim().toLowerCase());
      if (bahanInfo) {
        const hppPerUnit = getHargaJual(bahanInfo);
        const volMl = ukuran_ml > 0 ? ((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100) * ukuran_ml : (r.volume_ml || 0);
        defaultHppRacikan += (hppPerUnit * volMl);
      }
    });
  }

  let defaultHppKemasanUtama = 0;
  if (sizeData.bahan) {
    if (sizeData.bahan.is_fullset && sizeData.bahan.fullset_nama) {
      const fullsetMatch = fullsetListPrice.find(x => (x.nama || '').trim().toLowerCase() === (sizeData.bahan.fullset_nama || '').trim().toLowerCase());
      if (fullsetMatch) {
        defaultHppKemasanUtama += getHargaJual(fullsetMatch);
      }
    } else {
      if (sizeData.bahan.botol_nama) {
        const b = botolList.find(x => (x.nama || '').trim().toLowerCase() === (sizeData.bahan.botol_nama || '').trim().toLowerCase());
        if (b) defaultHppKemasanUtama += getHargaJual(b);
      }
      if (sizeData.bahan.tutup_nama) {
        const t = tutupList.find(x => (x.nama || '').trim().toLowerCase() === (sizeData.bahan.tutup_nama || '').trim().toLowerCase());
        if (t) defaultHppKemasanUtama += getHargaJual(t);
      }
      if (sizeData.bahan.spray_nama) {
        const s = sprayList.find(x => (x.nama || '').trim().toLowerCase() === (sizeData.bahan.spray_nama || '').trim().toLowerCase());
        if (s) defaultHppKemasanUtama += getHargaJual(s);
      }
    }

    if (Array.isArray(sizeData.bahan.item_tambahan)) {
      sizeData.bahan.item_tambahan.forEach(it => {
        const matched = itemTambahanList.find(x => (x.nama || '').trim().toLowerCase() === (it.nama || '').trim().toLowerCase());
        if (matched) {
          defaultHppKemasanUtama += (getHargaJual(matched) * (it.qty_per_pcs || 1));
        }
      });
    }
  }

  const getJasaProduksi = (ukuran) => {
    if (ukuran === 0) return 0;
    if (ukuran <= 35) return 3000;
    if (ukuran <= 105) return 4000;
    if (ukuran <= 255) return 5000;
    return 6000;
  };

  const defaultJasa = getJasaProduksi(ukuran_ml);
  const finalJasa = (sizeData.bahan?.custom_jasa !== undefined && sizeData.bahan?.custom_jasa !== null && sizeData.bahan?.custom_jasa !== '') 
    ? Number(sizeData.bahan.custom_jasa) 
    : defaultJasa;

  const hppRacikanFinal = defaultHppRacikan / 1000;
  const hppKemasanFinal = defaultHppKemasanUtama;

  const finalHpp = (sizeData.bahan?.custom_hpp !== undefined && sizeData.bahan?.custom_hpp !== null && sizeData.bahan?.custom_hpp !== '')
    ? Number(sizeData.bahan.custom_hpp)
    : (hppRacikanFinal + hppKemasanFinal);

  const totalHppWithJasa = finalHpp + finalJasa;

  // Harga Jual Akhir
  let finalHargaJual = 0;
  if (sizeData.bahan?.custom_harga_jual !== undefined && sizeData.bahan?.custom_harga_jual !== null && sizeData.bahan?.custom_harga_jual !== '') {
    finalHargaJual = Number(sizeData.bahan.custom_harga_jual);
  } else if (sizeData.bahan?.harga_jual_per_pcs) {
    finalHargaJual = Number(sizeData.bahan.harga_jual_per_pcs);
  } else {
    const margin = (sizeData.bahan?.custom_margin !== undefined && sizeData.bahan?.custom_margin !== null && sizeData.bahan?.custom_margin !== '')
      ? Number(sizeData.bahan.custom_margin)
      : 30;
    finalHargaJual = Math.round(totalHppWithJasa * (1 + margin / 100));
  }

  return {
    ukuran_ml: ukuran_ml || rawSize,
    jasa: Math.round(finalJasa),
    hargaJual: Math.round(finalHargaJual),
  };
};

/**
 * Directly generate and open aesthetic A4 PDF for Brand Price List
 */
export async function exportBrandPriceListPDF({
  activeBrand,
  botolList = [],
  tutupList = [],
  sprayList = [],
  masterBahanList = [],
  itemTambahanList = [],
  fullsetList = [],
  selectedVariantNames = 'all',
}) {
  if (!activeBrand) return;

  // 1. Open blank tab immediately to avoid popup blocker
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Mempersiapkan PDF - ${activeBrand.name}...</title>
          <style>
            body {
              margin: 0;
              padding: 0;
              height: 100vh;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              background-color: #FFFDF9;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
              color: #302621;
            }
            .spinner {
              width: 50px;
              height: 50px;
              border: 4px solid #E6D5B8;
              border-top: 4px solid #8C6208;
              border-radius: 50%;
              animation: spin 0.8s linear infinite;
              margin-bottom: 20px;
            }
            h2 {
              margin: 0 0 8px 0;
              font-size: 18px;
              font-weight: 800;
              letter-spacing: -0.02em;
            }
            p {
              margin: 0;
              font-size: 13px;
              color: #7A695B;
            }
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          </style>
        </head>
        <body>
          <div class="spinner"></div>
          <h2>Menyiapkan Dokumen PDF A4...</h2>
          <p>Memuat katalog harga resmi brand <strong>${activeBrand.name}</strong></p>
        </body>
      </html>
    `);
  }

  try {
    // 2. Load Assets (Use requested public/logo.png)
    const [logoB64, stempelB64, ttdB64, companySettingsList] = await Promise.all([
      loadImageAsBase64('/logo.png'),
      loadImageAsBase64('/stempel-invoice.png'),
      loadImageAsBase64('/ttd-invoice.png'),
      base44.entities.CompanySettings.list().catch(() => [])
    ]);

    const localSettings = companySettingsList?.[0] || {};
    const companySettings = {
      nama_perusahaan: localSettings.nama_perusahaan || 'PT. SATALA DERMATECH ESSENTIAL',
      alamat_perusahaan: localSettings.alamat_perusahaan || 'Jalan aster kavling Paspampres, Kota Batu, Kec. Ciomas, Kabupaten Bogor, Jawa Barat',
      nama_direktur: localSettings.nama_direktur || 'Umar Syarif',
    };

    const now = new Date();
    const romanMonth = ROMAN_MONTHS[now.getMonth()];
    const cleanBrandSlug = (activeBrand?.name || 'BRAND').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    const docNo = `PNH-${cleanBrandSlug}-${romanMonth}-${now.getFullYear()}`;

    // 3. Filter & Sort Data:
    const variantsToUse = (activeBrand.variants || []).filter(v => {
      if (selectedVariantNames === 'all' || !Array.isArray(selectedVariantNames)) return true;
      return selectedVariantNames.includes(v.name);
    });

    // A) Varian diurutkan sesuai abjad (A -> Z)
    const sortedVariants = [...variantsToUse].sort((a, b) => 
      (a.name || '').localeCompare(b.name || '', 'id', { sensitivity: 'base' })
    );

    const rows = [];
    let counter = 1;

    sortedVariants.forEach(variant => {
      const sizes = variant.sizes || [];
      if (sizes.length === 0) {
        rows.push({
          no: counter++,
          variantName: variant.name,
          sizeText: 'Standar',
          jasa: 0,
          hargaJual: 0,
        });
      } else {
        const calculatedSizes = sizes.map(s => {
          const calc = calculateSizePricing({
            sizeData: s,
            botolList,
            tutupList,
            sprayList,
            bahanList: masterBahanList,
            itemTambahanList,
            fullsetListPrice: fullsetList,
          });
          return {
            originalSize: s,
            ...calc
          };
        });

        // B) Diurutkan dari harga terkecil ke terbesar (dan ukuran ml terkecil)
        calculatedSizes.sort((a, b) => {
          if (a.hargaJual !== b.hargaJual) {
            return a.hargaJual - b.hargaJual;
          }
          return (Number(a.ukuran_ml) || 0) - (Number(b.ukuran_ml) || 0);
        });

        calculatedSizes.forEach(item => {
          const sizeStr = String(item.ukuran_ml).toLowerCase().includes('ml') 
            ? String(item.ukuran_ml) 
            : `${item.ukuran_ml} ml`;

          rows.push({
            no: counter++,
            variantName: variant.name,
            sizeText: sizeStr,
            jasa: item.jasa,
            hargaJual: item.hargaJual,
          });
        });
      }
    });

    // 4. Initialize A4 jsPDF in Portrait (Standard A4: 210 x 297 mm)
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pageW = 210;
    const pageH = 297;
    const marginL = 13;
    const marginR = 13;
    let y = 14;

    // Header Logo (/logo.png)
    if (logoB64) {
      doc.addImage(logoB64, 'PNG', marginL, y - 2, 16, 16, '', 'FAST');
    }

    // Header Satala Dermatech
    const headerTextX = logoB64 ? marginL + 20 : marginL;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(48, 38, 33);
    doc.text(companySettings.nama_perusahaan, headerTextX, y + 2.5);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(140, 98, 8);
    doc.text('ORIGINAL EQUIPMENT MANUFACTURER & PERFUMERY LAB', headerTextX, y + 6.5);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(115, 105, 95);
    doc.text(companySettings.alamat_perusahaan, headerTextX, y + 10.5);

    // Document Title (Right)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(140, 98, 8);
    doc.text('DAFTAR HARGA PRODUK & JASA', pageW - marginR, y + 2, { align: 'right' });
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(90, 80, 75);
    doc.text(`No: ${docNo}`, pageW - marginR, y + 6.5, { align: 'right' });
    doc.text(`Tanggal: ${formatTanggalIndo(now)}`, pageW - marginR, y + 10.5, { align: 'right' });

    y += 14;
    // Elegant Gold Accent Divider Line
    doc.setDrawColor(212, 175, 55);
    doc.setLineWidth(0.8);
    doc.line(marginL, y, pageW - marginR, y);

    y += 4;
    // Brand Info Box (Soft cream background with warm border)
    doc.setFillColor(249, 245, 236);
    doc.roundedRect(marginL, y, pageW - marginL - marginR, 9.5, 2, 2, 'F');
    doc.setDrawColor(230, 213, 184);
    doc.setLineWidth(0.3);
    doc.roundedRect(marginL, y, pageW - marginL - marginR, 9.5, 2, 2, 'S');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(48, 38, 33);
    doc.text(`BRAND: ${activeBrand.name.toUpperCase()}`, marginL + 4, y + 4);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(110, 95, 85);
    doc.text(
      `Status: ${activeBrand.is_haki ? 'Sudah HAKI' : 'Belum HAKI'}   |   Total Varian: ${activeBrand.variants?.length || 0} Varian   |   Total Ukuran: ${rows.length} Item`,
      marginL + 4,
      y + 7.5
    );

    y += 13;

    // 5 Columns: No | Nama Varian Produk | Ukuran | Jasa Produksi | Harga Jual Akhir
    const tableHeaders = [
      ['No', 'Nama Varian Produk', 'Ukuran', 'Jasa Produksi', 'Harga Jual Akhir']
    ];

    const tableBody = rows.map(r => [
      r.no,
      r.variantName,
      r.sizeText,
      `Rp ${fmt(r.jasa)}`,
      `Rp ${fmt(r.hargaJual)}`
    ]);

    autoTable(doc, {
      head: tableHeaders,
      body: tableBody,
      startY: y,
      theme: 'grid',
      margin: { top: 12, left: marginL, right: marginR, bottom: 15 },
      headStyles: {
        fillColor: [48, 38, 33],
        textColor: [212, 175, 55],
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center',
        valign: 'middle',
        cellPadding: { top: 2, bottom: 2, left: 2.5, right: 2.5 }
      },
      styles: {
        fontSize: 7.5,
        font: 'helvetica',
        cellPadding: { top: 1.6, bottom: 1.6, left: 2.5, right: 2.5 },
        textColor: [45, 40, 35],
        lineColor: [230, 225, 215],
        lineWidth: 0.15,
        valign: 'middle'
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 12 },
        1: { fontStyle: 'bold', cellWidth: 84 },
        2: { halign: 'center', fontStyle: 'bold', textColor: [140, 98, 8], cellWidth: 24 },
        3: { halign: 'right', fontStyle: 'bold', textColor: [90, 80, 70], cellWidth: 32 },
        4: { halign: 'right', fontStyle: 'bold', textColor: [48, 38, 33], cellWidth: 32 },
      },
      alternateRowStyles: {
        fillColor: [253, 251, 247]
      },
      didDrawPage: (data) => {
        // Page Number & Footer
        const totalPages = doc.internal.getNumberOfPages();
        const currentPage = data.pageNumber;
        
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(150, 140, 130);
        
        const footerY = pageH - 7;
        doc.text(
          `${companySettings.nama_perusahaan} • Dokumen Resmi Penawaran Harga Produk`,
          marginL,
          footerY
        );
        doc.text(
          `Halaman ${currentPage} dari ${totalPages}`,
          pageW - marginR,
          footerY,
          { align: 'right' }
        );
      }
    });

    // 5. Signature & Notes Section
    let finalY = doc.lastAutoTable.finalY + 6;
    if (finalY > pageH - 42) {
      doc.addPage();
      finalY = 16;
    }

    // Left: Notes
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(48, 38, 33);
    doc.text('Catatan & Ketentuan Penawaran:', marginL, finalY);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(110, 100, 90);
    doc.text('1. Harga Jual Akhir di atas merupakan harga resmi maklon per pcs yang mencakup formulasi & kemasan standar.', marginL, finalY + 4);
    doc.text('2. Jasa Produksi tertera di sebelah kiri harga jual akhir sebagai transparansi komponen pengerjaan pabrik.', marginL, finalY + 7.5);
    doc.text('3. Minimum Order Quantity (MOQ) dan termin pembayaran mengikuti kesepakatan kerjasama resmi.', marginL, finalY + 11);

    // Right: Signature & Stamp
    const sigCenterX = pageW - marginR - 35;
    doc.setFontSize(7.5);
    doc.setTextColor(100, 90, 80);
    doc.text(`Bogor, ${formatTanggalIndo(now)}`, sigCenterX, finalY, { align: 'center' });

    // Stempel Satala
    if (stempelB64) {
      doc.addImage(stempelB64, 'PNG', sigCenterX - 18, finalY + 1, 20, 20, '', 'FAST');
    }
    // TTD Direktur
    if (ttdB64) {
      doc.addImage(ttdB64, 'PNG', sigCenterX - 14, finalY + 2, 28, 16, '', 'FAST');
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(48, 38, 33);
    doc.text(companySettings.nama_direktur || 'Umar Syarif', sigCenterX, finalY + 22, { align: 'center' });

    doc.setDrawColor(200, 190, 180);
    doc.setLineWidth(0.3);
    doc.line(sigCenterX - 25, finalY + 23, sigCenterX + 25, finalY + 23);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(140, 130, 120);
    doc.text('DIREKTUR', sigCenterX, finalY + 26, { align: 'center' });

    // 6. Output PDF to the opened window
    doc.setProperties({
      title: `Daftar_Harga_${cleanBrandSlug}.pdf`
    });

    const blobUrl = doc.output('bloburl');
    if (printWindow) {
      printWindow.location.href = blobUrl;
    } else {
      window.open(blobUrl, '_blank');
    }
  } catch (err) {
    console.error('Error generating Brand Price List PDF:', err);
    if (printWindow) {
      printWindow.document.body.innerHTML = `
        <div style="font-family: sans-serif; text-align: center; margin-top: 20%; color: #b91c1c;">
          <h3>Terjadi Kesalahan saat Membuat PDF</h3>
          <p>${err.message || 'Silakan coba lagi.'}</p>
        </div>
      `;
    }
  }
}
